import { useEffect, useRef } from 'react'
import { useBaiduMap } from './BaiduMap'

export interface BaiduMarkerProps {
    position: { lat: number; lng: number }
    icon?: {
        url: string
        scaledSize?: { width: number; height: number }
    }
    title?: string
    opacity?: number
    animation?: 'BOUNCE' | 'DROP' | null
    onClick?: () => void
}

const BaiduMarker: React.FC<BaiduMarkerProps> = ({
    position,
    icon,
    title,
    opacity = 1,
    animation,
    onClick
}) => {
    const map = useBaiduMap()
    const markerRef = useRef<any>(null)

    useEffect(() => {
        if (!map || !window.BMapGL) return

        const point = new window.BMapGL.Point(position.lng, position.lat)
        let marker: any

        if (icon?.url && window.BMapGL.Label) {
            const size = icon.scaledSize || { width: 32, height: 32 }
            marker = new window.BMapGL.Label('\u200B', {
                position: point,
                offset: new window.BMapGL.Size(-size.width / 2, -size.height / 2)
            })
            marker.setStyle({
                width: `${size.width}px`,
                height: `${size.height}px`,
                padding: '0',
                border: '0',
                backgroundColor: 'transparent',
                backgroundImage: `url(${icon.url})`,
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'contain',
                cursor: onClick ? 'pointer' : 'default',
                opacity
            })
            if (title && typeof marker.setTitle === 'function') marker.setTitle(title)
        } else {
            marker = new window.BMapGL.Marker(point, { title: title || '' })
            // JSAPI GL Marker에는 setOpacity가 없는 버전이 있으므로 기능 확인 후 호출한다.
            if (opacity !== 1 && typeof marker.setOpacity === 'function') {
                marker.setOpacity(opacity)
            }
            if (animation === 'BOUNCE' && typeof marker.setAnimation === 'function') {
                marker.setAnimation(window.BMAP_ANIMATION_BOUNCE)
            }
        }

        if (onClick) {
            marker.addEventListener('click', onClick)
        }

        map.addOverlay(marker)
        markerRef.current = marker

        return () => {
            if (onClick) {
                marker.removeEventListener('click', onClick)
            }
            map.removeOverlay(marker)
            markerRef.current = null
        }
    }, [map, position.lat, position.lng, icon?.url, icon?.scaledSize?.width, icon?.scaledSize?.height, title, opacity, animation, onClick])

    return null
}

export default BaiduMarker
